// Fixed, pickable icon palette for Category.icon. Mirrors the icon+color-per-type
// pattern in investmentTypes.js, but categories are user-extensible (custom categories +
// backend-persisted icon per row), so this file is the picker palette + string-key
// resolver rather than a closed lookup table.
//
// `value` here must match a key in backend/server.py's DEFAULT_CATEGORY_ICONS for the
// 12 default categories to render correctly out of the box.
import {
    GraduationCap, Clapperboard, UtensilsCrossed, ShoppingBasket, HeartPulse, Shapes,
    Home, Banknote, ShoppingBag, Car, Plane, PlugZap,
    Tag, Coffee, Pizza, Fuel, Bus, Train, Bike, Dumbbell, Gamepad2, Music, Film,
    BookOpen, Baby, PawPrint, Gift, Wrench, Shirt, Smartphone, Wifi, Droplet, Flame,
    Landmark, PiggyBank, CreditCard, Receipt, Stethoscope, Pill, Palette, Briefcase,
    Hotel, Wallet, TrendingUp, Sparkles, Dog, Cat, Cake, ShoppingCart, Sofa, Lightbulb,
    Umbrella, Trees, Camera, Ticket, Newspaper, Award,
} from "lucide-react";

export const CATEGORY_ICONS = [
    // Defaults (must stay in sync with backend DEFAULT_CATEGORY_ICONS)
    { value: "graduation-cap", label: "Education", icon: GraduationCap, color: "#6366F1" },
    { value: "clapperboard", label: "Entertainment", icon: Clapperboard, color: "#8B5CF6" },
    { value: "utensils-crossed", label: "Food & Drink", icon: UtensilsCrossed, color: "#F59E0B" },
    { value: "shopping-basket", label: "Groceries", icon: ShoppingBasket, color: "#10B981" },
    { value: "heart-pulse", label: "Health", icon: HeartPulse, color: "#EF4444" },
    { value: "shapes", label: "Other", icon: Shapes, color: "#64748B" },
    { value: "home", label: "Rent / Mortgage", icon: Home, color: "#0EA5E9" },
    { value: "banknote", label: "Salary", icon: Banknote, color: "#22C55E" },
    { value: "shopping-bag", label: "Shopping", icon: ShoppingBag, color: "#EC4899" },
    { value: "car", label: "Transport", icon: Car, color: "#06B6D4" },
    { value: "plane", label: "Vacation", icon: Plane, color: "#14B8A6" },
    { value: "plug-zap", label: "Utilities", icon: PlugZap, color: "#F97316" },
    // Extra picker options for custom categories
    { value: "tag", label: "Tag", icon: Tag, color: "#94A3B8" },
    { value: "coffee", label: "Coffee", icon: Coffee, color: "#92400E" },
    { value: "pizza", label: "Pizza", icon: Pizza, color: "#DC2626" },
    { value: "fuel", label: "Fuel", icon: Fuel, color: "#EA580C" },
    { value: "bus", label: "Bus", icon: Bus, color: "#2563EB" },
    { value: "train", label: "Train", icon: Train, color: "#4F46E5" },
    { value: "bike", label: "Bike", icon: Bike, color: "#16A34A" },
    { value: "dumbbell", label: "Fitness", icon: Dumbbell, color: "#DC2626" },
    { value: "gamepad-2", label: "Gaming", icon: Gamepad2, color: "#7C3AED" },
    { value: "music", label: "Music", icon: Music, color: "#DB2777" },
    { value: "film", label: "Movies", icon: Film, color: "#9333EA" },
    { value: "book-open", label: "Books", icon: BookOpen, color: "#0891B2" },
    { value: "baby", label: "Kids", icon: Baby, color: "#F472B6" },
    { value: "paw-print", label: "Pets", icon: PawPrint, color: "#A16207" },
    { value: "dog", label: "Dog", icon: Dog, color: "#B45309" },
    { value: "cat", label: "Cat", icon: Cat, color: "#C2410C" },
    { value: "gift", label: "Gifts", icon: Gift, color: "#E11D48" },
    { value: "wrench", label: "Repairs", icon: Wrench, color: "#475569" },
    { value: "shirt", label: "Clothing", icon: Shirt, color: "#0D9488" },
    { value: "smartphone", label: "Phone", icon: Smartphone, color: "#4338CA" },
    { value: "wifi", label: "Internet", icon: Wifi, color: "#0284C7" },
    { value: "droplet", label: "Water", icon: Droplet, color: "#0EA5E9" },
    { value: "flame", label: "Gas", icon: Flame, color: "#F97316" },
    { value: "lightbulb", label: "Electricity", icon: Lightbulb, color: "#EAB308" },
    { value: "landmark", label: "Taxes", icon: Landmark, color: "#334155" },
    { value: "piggy-bank", label: "Savings", icon: PiggyBank, color: "#EC4899" },
    { value: "credit-card", label: "Fees", icon: CreditCard, color: "#6366F1" },
    { value: "receipt", label: "Bills", icon: Receipt, color: "#78716C" },
    { value: "stethoscope", label: "Medical", icon: Stethoscope, color: "#DC2626" },
    { value: "pill", label: "Pharmacy", icon: Pill, color: "#DB2777" },
    { value: "palette", label: "Hobbies", icon: Palette, color: "#8B5CF6" },
    { value: "briefcase", label: "Business", icon: Briefcase, color: "#1E40AF" },
    { value: "hotel", label: "Hotel", icon: Hotel, color: "#7C2D12" },
    { value: "wallet", label: "Wallet", icon: Wallet, color: "#059669" },
    { value: "trending-up", label: "Investing", icon: TrendingUp, color: "#16A34A" },
    { value: "sparkles", label: "Self-care", icon: Sparkles, color: "#D946EF" },
    { value: "cake", label: "Celebrations", icon: Cake, color: "#F43F5E" },
    { value: "shopping-cart", label: "Cart", icon: ShoppingCart, color: "#EA580C" },
    { value: "sofa", label: "Furniture", icon: Sofa, color: "#A855F7" },
    { value: "umbrella", label: "Insurance", icon: Umbrella, color: "#0369A1" },
    { value: "trees", label: "Outdoors", icon: Trees, color: "#15803D" },
    { value: "camera", label: "Photography", icon: Camera, color: "#52525B" },
    { value: "ticket", label: "Events", icon: Ticket, color: "#C026D3" },
    { value: "newspaper", label: "News", icon: Newspaper, color: "#57534E" },
    { value: "award", label: "Rewards", icon: Award, color: "#CA8A04" },
];

const ICON_MAP = Object.fromEntries(CATEGORY_ICONS.map(i => [i.value, i]));

// Falls back to a name-based guess (for legacy rows with no icon), then to a generic tag icon.
const NAME_FALLBACKS = {
    Education: "graduation-cap", Entertainment: "clapperboard", "Food & Drink": "utensils-crossed",
    Groceries: "shopping-basket", Health: "heart-pulse", Other: "shapes", "Rent / Mortgage": "home",
    Salary: "banknote", Shopping: "shopping-bag", Transport: "car", Vacation: "plane", Utilities: "plug-zap",
};

/** Resolve a category (or category name) to { value, label, icon: LucideComponent }. */
export function resolveCategoryIcon(category) {
    const iconKey = typeof category === "string" ? NAME_FALLBACKS[category] : (category?.icon || NAME_FALLBACKS[category?.name]);
    return ICON_MAP[iconKey] || ICON_MAP.tag;
}

/**
 * Many places (Expenses, Transactions, QuickAdd) only have a category *name* string
 * (e.g. Expense.category), not the full Category row with its persisted `icon`. This
 * looks the name up against the live `categories` list (from useCategories()) to find
 * the real per-category icon — including custom icons a user picked — falling back to
 * the name-based guess if the category was since renamed/deleted.
 */
export function iconForCategoryName(categories, name) {
    const match = categories?.find(c => c.name === name);
    return resolveCategoryIcon(match || name);
}
